import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ErrorBoundary } from 'react-error-boundary';

import { Midground } from '../../atoms';
import { GenericError } from '../../molecules';
import { Sidebar, Navigation } from '../../organisms';
import { SidebarProvider, SidebarContext } from '../../../providers';

import { userShape, spaceShape } from '../../../propsShape';

const DefaultLayoutContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: fixed;
  inset: 0px;

  @media (max-width: 768px) {
    padding: 0;
    flex-direction: column;
    flex-wrap: wrap;

    padding-top: env(safe-area-inset-top);
  }
`;

const DefaultLayout = ({
  currentOrganization,
  organizations,
  selectOrganization,
  groupedConversations,
  deleteConversation,
  updateConversation,
  openNextConversation,
  fetchNextConversationsPage,
  users,
  user = null,
  children = null,
  logout = null,
  spaces = null,
  isAgentic = false
}) => {
  const handleNavigationScroll = () => {
    fetchNextConversationsPage?.({ cancelRefetch: false });
  };

  return (
    <SidebarProvider>
      <DefaultLayoutContainer>
        <SidebarContext.Consumer>
          {({ sidebarCollapsed, setSidebarCollapsed }) => (
            <>
              <Sidebar
                collapsed={sidebarCollapsed}
                collapseSidebar={() => setSidebarCollapsed(true)}
                user={user}
                currentOrganization={currentOrganization}
                organizations={organizations}
                selectOrganization={selectOrganization}
                onLogoutClick={logout}
                onNavigationScroll={handleNavigationScroll}
                isReadOnly={!user?.canAsk}
                isAgentic={isAgentic}
              >
                <Navigation
                  groupedConversations={groupedConversations}
                  deleteConversation={deleteConversation}
                  updateConversation={updateConversation}
                  openNextConversation={openNextConversation}
                  currentUser={user}
                  users={users}
                  spaces={spaces}
                  isReadOnly={!user?.canAsk}
                  isAgentic={isAgentic}
                />
              </Sidebar>
              <Midground>
                <ErrorBoundary fallback={<GenericError />}>{children}</ErrorBoundary>
              </Midground>
            </>
          )}
        </SidebarContext.Consumer>
      </DefaultLayoutContainer>
    </SidebarProvider>
  );
};

const organizationProps = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string
});

DefaultLayout.propTypes = {
  user: userShape,
  users: PropTypes.arrayOf(userShape).isRequired,
  groupedConversations: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayTitle: PropTypes.string.isRequired
      })
    )
  }).isRequired,
  deleteConversation: PropTypes.func.isRequired,
  updateConversation: PropTypes.func.isRequired,
  openNextConversation: PropTypes.func.isRequired,
  currentOrganization: organizationProps.isRequired,
  organizations: PropTypes.arrayOf(organizationProps).isRequired,
  selectOrganization: PropTypes.func.isRequired,
  fetchNextConversationsPage: PropTypes.func.isRequired,
  logout: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  spaces: PropTypes.arrayOf(spaceShape),
  isAgentic: PropTypes.bool
};

export { DefaultLayout };
